import { useState, useCallback } from 'react';
import { convertAudioToText, convertImageToText } from '../../../services/gemini';
import { extractPdfText } from '../utils/pdfTextExtractor';

const useFileConversion = (quillRef, showError, showSuccess, clearAlerts) => {
    const [isConvertingAudio, setIsConvertingAudio] = useState(false);
    const [isConvertingImage, setIsConvertingImage] = useState(false);
    const [isConvertingPdf, setIsConvertingPdf] = useState(false);
    const [audioUploadModalVisible, setAudioUploadModalVisible] = useState(false);
    const [imageUploadModalVisible, setImageUploadModalVisible] = useState(false);
    const [pdfUploadModalVisible, setPdfUploadModalVisible] = useState(false);
    const [resultModalVisible, setResultModalVisible] = useState(false);
    const [convertedText, setConvertedText] = useState('');
    const [resultType, setResultType] = useState(''); // 'audio', 'image', or 'pdf'
    const [isLoadingModalVisible, setIsLoadingModalVisible] = useState(false);
    const [loadingMessage, setLoadingMessage] = useState('');

    const handleAudioToText = useCallback(async (audioFile) => {
        if (!audioFile) {
            showError("Please select an audio file.");
            return;
        }

        // Validate file type - MP3 files can have different MIME types
        const validAudioTypes = [
            'audio/mp3',
            'audio/mpeg',
            'audio/mpeg3',
            'audio/x-mpeg-3',
            'audio/wav',
            'audio/wave',
            'audio/x-wav',
            'audio/m4a',
            'audio/mp4',
            'audio/ogg',
            'audio/webm'
        ];

        // Also check file extension as fallback
        const fileName = audioFile.name.toLowerCase();
        const validExtensions = ['.mp3', '.wav', '.m4a', '.ogg', '.webm'];
        const hasValidExtension = validExtensions.some(ext => fileName.endsWith(ext));

        if (!validAudioTypes.includes(audioFile.type) && !hasValidExtension) {
            showError("Please select a valid audio file (MP3, WAV, M4A, OGG, or WebM).");
            return;
        }

        // Validate file size (max 25MB)
        const maxSize = 25 * 1024 * 1024; // 25MB
        if (audioFile.size > maxSize) {
            showError("Audio file size must be less than 25MB.");
            return;
        }

        clearAlerts();
        setIsConvertingAudio(true);
        setIsLoadingModalVisible(true);
        setLoadingMessage("Converting audio to text...");

        try {
            const transcribedText = await convertAudioToText(audioFile);
            setConvertedText(transcribedText);
            setResultType('audio');
            setAudioUploadModalVisible(false);
            setResultModalVisible(true);
            showSuccess("Audio converted to text successfully!");
        } catch (error) {
            console.error("Audio conversion error:", error);
            showError(error.message || "Failed to convert audio to text. Please try again.");
        } finally {
            setIsConvertingAudio(false);
            setIsLoadingModalVisible(false);
        }
    }, [showError, showSuccess, clearAlerts]);

    const handleImageToText = useCallback(async (imageFile) => {
        if (!imageFile) {
            showError("Please select an image file.");
            return;
        }

        // Validate file type
        const validImageTypes = [
            'image/jpeg',
            'image/jpg',
            'image/png',
            'image/gif',
            'image/webp',
            'image/bmp',
            'image/tiff'
        ];

        // Also check file extension as fallback
        const fileName = imageFile.name.toLowerCase();
        const validExtensions = ['.jpg', '.jpeg', '.png', '.gif', '.webp', '.bmp', '.tiff'];
        const hasValidExtension = validExtensions.some(ext => fileName.endsWith(ext));

        if (!validImageTypes.includes(imageFile.type) && !hasValidExtension) {
            showError("Please select a valid image file (JPEG, PNG, GIF, WebP, BMP, or TIFF).");
            return;
        }

        // Validate file size (max 20MB)
        const maxSize = 20 * 1024 * 1024; // 20MB
        if (imageFile.size > maxSize) {
            showError("Image file size must be less than 20MB.");
            return;
        }

        clearAlerts();
        setIsConvertingImage(true);
        setIsLoadingModalVisible(true);
        setLoadingMessage("Extracting text from image...");

        try {
            const extractedText = await convertImageToText(imageFile);
            setConvertedText(extractedText);
            setResultType('image');
            setImageUploadModalVisible(false);
            setResultModalVisible(true);
            showSuccess("Text extracted from image successfully!");
        } catch (error) {
            console.error("Image conversion error:", error);
            showError(error.message || "Failed to extract text from image. Please try again.");
        } finally {
            setIsConvertingImage(false);
            setIsLoadingModalVisible(false);
        }
    }, [showError, showSuccess, clearAlerts]);

    const handlePdfToText = useCallback(async (pdfFile) => {
        if (!pdfFile) {
            showError("Please select a PDF file.");
            return;
        }

        const fileName = pdfFile.name.toLowerCase();
        const hasValidExtension = fileName.endsWith('.pdf');

        if (pdfFile.type !== 'application/pdf' && !hasValidExtension) {
            showError("Please select a valid PDF file.");
            return;
        }

        const maxSize = 20 * 1024 * 1024;
        if (pdfFile.size > maxSize) {
            showError("PDF file size must be less than 20MB.");
            return;
        }

        clearAlerts();
        setIsConvertingPdf(true);
        setIsLoadingModalVisible(true);
        setLoadingMessage("Extracting text from PDF and using OCR when needed...");

        try {
            const extractedText = await extractPdfText(pdfFile);
            setConvertedText(extractedText);
            setResultType('pdf');
            setPdfUploadModalVisible(false);
            setResultModalVisible(true);
            showSuccess("Text extracted from PDF successfully!");
        } catch (error) {
            console.error("PDF conversion error:", error);
            showError(error.message || "Failed to extract text from PDF. Please try again.");
        } finally {
            setIsConvertingPdf(false);
            setIsLoadingModalVisible(false);
        }
    }, [showError, showSuccess, clearAlerts]);

    const handleInsertText = useCallback(() => {
        if (!convertedText || !quillRef.current) return;

        const quill = quillRef.current.getEditor();
        const range = quill.getSelection() || { index: quill.getLength() };

        // Insert the converted text at cursor position or at the end
        quill.insertText(range.index, convertedText);

        // Close the modal
        setResultModalVisible(false);
        setConvertedText('');
        setResultType('');

        showSuccess("Text inserted into editor!");
    }, [convertedText, quillRef, showSuccess]);

    const handleCopyText = useCallback(() => {
        if (!convertedText) return;

        navigator.clipboard.writeText(convertedText)
            .then(() => {
                showSuccess("Text copied to clipboard!");
                setResultModalVisible(false);
                setConvertedText('');
                setResultType('');
            })
            .catch(() => {
                showError("Failed to copy text to clipboard.");
            });
    }, [convertedText, showSuccess, showError]);

    return {
        isConvertingAudio,
        isConvertingImage,
        isConvertingPdf,
        audioUploadModalVisible,
        imageUploadModalVisible,
        pdfUploadModalVisible,
        resultModalVisible,
        convertedText,
        resultType,
        isLoadingModalVisible,
        loadingMessage,
        setAudioUploadModalVisible,
        setImageUploadModalVisible,
        setPdfUploadModalVisible,
        setResultModalVisible,
        handleAudioToText,
        handleImageToText,
        handlePdfToText,
        handleInsertText,
        handleCopyText,
    };
};

export default useFileConversion;
